import React, { useEffect, useState, useCallback } from 'react';
import { AlertTriangle, X } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * LowStockAlert — compact admin-side alert banner, polls /api/admin/low-stock every 60s.
 * Shows nothing when no alerts; otherwise a colored banner with item list (collapsed).
 */
const LowStockAlert = ({ token, threshold = 5 }) => {
  const [alerts, setAlerts] = useState([]);
  const [dismissed, setDismissed] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const fetchAlerts = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/low-stock?threshold=${threshold}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAlerts(res.data.alerts || []);
    } catch (e) { /* silent */ }
  }, [token, threshold]);

  useEffect(() => {
    fetchAlerts();
    const id = setInterval(fetchAlerts, 60000);
    return () => clearInterval(id);
  }, [fetchAlerts]);

  if (dismissed || alerts.length === 0) return null;

  return (
    <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-300 rounded-2xl p-4 mb-4" data-testid="low-stock-alert">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-full bg-amber-500 text-white flex items-center justify-center flex-shrink-0">
          <AlertTriangle className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <p className="font-semibold text-amber-900">
              {alerts.length} ürünün stoğu kritik seviyede (≤{threshold})
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setExpanded(v => !v)}
                className="text-xs font-semibold text-amber-700 hover:text-amber-900 underline"
                data-testid="low-stock-toggle"
              >
                {expanded ? 'Gizle' : 'Detayları Göster'}
              </button>
              <button
                onClick={() => setDismissed(true)}
                className="text-amber-600 hover:text-amber-900"
                aria-label="Kapat"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
          {expanded && (
            <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-2">
              {alerts.slice(0, 20).map((a, i) => (
                <div key={`${a.type}-${a.name}-${i}`} className="flex items-center justify-between bg-white/70 rounded-lg px-3 py-1.5 text-sm">
                  <span className="text-amber-900 truncate">
                    <span className="text-[10px] uppercase tracking-wider mr-1.5 px-1.5 py-0.5 rounded bg-amber-200 text-amber-800">
                      {a.type === 'catering' ? 'Catering' : 'İçecek'}
                    </span>
                    {a.name}
                  </span>
                  <span className="font-bold text-amber-700 ml-2">{a.stock}</span>
                </div>
              ))}
              {alerts.length > 20 && (
                <p className="text-xs text-amber-700 italic md:col-span-2">+{alerts.length - 20} daha...</p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LowStockAlert;
